/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useMemo, useState } from 'react';

import Highcharts, { Options } from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

import { HIGHCHART_SETTINGS } from '../../../const';
import { LineChartPoint } from '../../../types/chart';

Highcharts.setOptions(HIGHCHART_SETTINGS);

type SeriesKey = 'v' | 'vr' | 'vc' | 'c' | 'vi';

type Props = {
  data: LineChartPoint[];
  legends?: {
    v?: string;
    vi?: string;
    vc?: string;
    c?: string;
    vr?: string;
  };
};

type LineConfig = {
  key: SeriesKey;
  label: string;
  color: string;
  values: Array<[number, number]>;
};

const SERIES_STYLES: Record<SeriesKey, { color: string; defaultLabel: string }> = {
  v: {
    color: '#5ECBA1',
    defaultLabel: 'доходность по сигналам',
  },
  vr: {
    color: '#066b8a',
    defaultLabel: 'доходность без издержек',
  },
  vc: {
    color: '#FF6B69',
    defaultLabel: 'доходность с учетом издержек *',
  },
  c: {
    color: '#066b8a',
    defaultLabel: 'доходность клиента',
  },
  vi: {
    color: '#9598a7',
    defaultLabel: 'бенчмарк',
  },
};

const NOTES: Partial<Record<SeriesKey, string>> = {
  vc: '* доходность с учетом средней цены исполнения сигналов и стоимости услуги Автоследование',
};

const SERIES_ORDER: SeriesKey[] = ['v', 'vr', 'vc', 'c', 'vi'];

const getSeriesValue = (point: LineChartPoint, key: SeriesKey) => point[key];

export const LineChart: React.FC<Props> = ({ data, legends }) => {
  const seriesConfig = useMemo<LineConfig[]>(() => {
    return SERIES_ORDER.reduce<LineConfig[]>((acc, key) => {
      const values = data.reduce<Array<[number, number]>>((items, point) => {
        const value = getSeriesValue(point, key);

        if (typeof value === 'number') {
          items.push([point.t, value]);
        }

        return items;
      }, []);

      if (values.length === 0) {
        return acc;
      }

      acc.push({
        key,
        label: legends?.[key] || SERIES_STYLES[key].defaultLabel,
        color: SERIES_STYLES[key].color,
        values,
      });

      return acc;
    }, []);
  }, [data, legends]);

  const [visibleSeries, setVisibleSeries] = useState<Record<SeriesKey, boolean>>({
    v: true,
    vr: true,
    vc: true,
    c: true,
    vi: true,
  });

  useEffect(() => {
    setVisibleSeries((prev) =>
      SERIES_ORDER.reduce<Record<SeriesKey, boolean>>((acc, key) => {
        acc[key] = prev[key] ?? true;
        return acc;
      }, {} as Record<SeriesKey, boolean>),
    );
  }, [seriesConfig]);

  const displayedSeries = useMemo(
    () => seriesConfig.filter((series) => visibleSeries[series.key]),
    [seriesConfig, visibleSeries],
  );

  const notes = useMemo(() => {
    return seriesConfig.filter((series) => NOTES[series.key]).map((series) => NOTES[series.key] as string);
  }, [seriesConfig]);

  const toggleSeries = (key: SeriesKey) => {
    setVisibleSeries((prev) => {
      const currentlyVisible = seriesConfig.filter((series) => prev[series.key]);
      if (prev[key] && currentlyVisible.length === 1) {
        return prev;
      }

      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  };

  const options: Options = useMemo(
    () => ({
      chart: {
        backgroundColor: 'transparent',
        spacing: [12, 0, 12, 0],
        style: {
          fontFamily: 'Gilroy',
          color: '#9598A7',
        },
      },
      xAxis: {
        type: 'datetime',
        crosshair: {
          color: '#EBE9EA',
          width: 1,
        },
        gridLineWidth: 1,
        gridLineColor: '#EBE9EA',
        labels: {
          style: {
            color: '#9598A7',
          },
          format: '{value:%d.%m.%Y}',
        },
        lineColor: '#EBE9EA',
        tickLength: 0,
        tickWidth: 0,
      },
      yAxis: {
        gridLineColor: '#EBE9EA',
        labels: {
          style: {
            color: '#9598A7',
          },
          formatter: function () {
            return `${Highcharts.numberFormat(Number(this.value), 0)}%`;
          },
        },
        title: {
          text: null,
        },
        lineColor: '#EBE9EA',
        lineWidth: 1,
        opposite: true,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      rangeSelector: {
        enabled: false,
      },
      navigator: {
        enabled: false,
      },
      scrollbar: {
        enabled: false,
      },
      title: {
        text: '',
      },
      tooltip: {
        backgroundColor: '#ffffff',
        borderColor: '#EBE9EA',
        borderRadius: 8,
        borderWidth: 1,
        shadow: false,
        padding: 12,
        split: false,
        shared: true,
        useHTML: true,
        formatter: function () {
          const points = this.points || [];

          if (!this.x || points.length === 0) {
            return false;
          }

          const date = Highcharts.dateFormat('%e %B %Y', Number(this.x));
          const rows = points
            .map((point) => {
              return `
                <div class="line-chart__tooltip-item">
                  <span class="line-chart__tooltip-dot" style="color:${point.color}">●</span>
                  <span class="line-chart__tooltip-name">${point.series.name}:</span>
                  <span class="line-chart__tooltip-value">${Highcharts.numberFormat(Number(point.y), 2)}%</span>
                </div>
              `;
            })
            .join('');

          return `
            <div class="line-chart__tooltip">
              <div class="line-chart__tooltip-date">${date}</div>
              ${rows}
            </div>
          `;
        },
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      series: displayedSeries.map((series) => ({
        type: 'line',
        name: series.label,
        color: series.color,
        data: series.values,
        threshold: null,
        lineWidth: series.key === 'vi' ? 2 : 3,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 4,
            },
          },
        },
      })),
    }),
    [displayedSeries],
  );

  return (
    <div className="line-chart">
      <HighchartsReact options={options} highcharts={Highcharts} />
      {seriesConfig.length > 0 && (
        <div className="line-chart__legend">
          {seriesConfig.map((series) => (
            <button
              key={series.key}
              type="button"
              className={`line-chart__legend-item ${
                !visibleSeries[series.key] ? 'line-chart__legend-item--hidden' : ''
              }`}
              onClick={() => toggleSeries(series.key)}
            >
              <span className="line-chart__legend-line" style={{ backgroundColor: series.color }} />
              <span className="line-chart__legend-label">{series.label}</span>
            </button>
          ))}
        </div>
      )}
      {notes.length > 0 && (
        <div className="line-chart__notes">
          {notes.map((note) => (
            <div key={note} className="line-chart__description">
              {note}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
